import { useState, useCallback, ReactNode, Fragment } from 'react'
import { common } from '@ocean/ui'
import { LegacyMenuRedirectRoute } from 'types'
import { PermissionUtils, UserUtils } from 'utils'
import { NotificationsNumbers } from 'core/environment/types'
import { LegacyMenuItem, StaRedirectParams } from 'core/staIntegration/types'
import DigitalDeliveryMenuItem from 'components/general/Sidebar/DigitalDeliveryMenuItem'
import { AccountTypes } from 'modules/Auth/types'
import AssessmentPlatformMenuItem from '../SectionsNav/AssessmentPlatformMenuItem'
import ProgramBuilderMenuItem from '../SectionsNav/ProgramBuilderMenuItem'
import { Wrapper, BranchWrapper, Title, Counter, List, ListItem } from './NavTree.styled'

const { Typography, Icon } = common

type NavBranchItemProps = {
  item: LegacyMenuItem
  onRedirect: (redirectParams: StaRedirectParams) => void
}

const NavBranchItem: React.FC<NavBranchItemProps> = ({ item, onRedirect }) => {
  const { text, pluginName, moduleName } = item
  const simulatorTrainingInstructorLedMenuDenied =
    PermissionUtils.simulatorTrainingInstructorLedMenuDenied()
  const simulatorTrainingStudentLedMenuDenied =
    PermissionUtils.simulatorTrainingStudentLedMenuDenied()

  function handleRedirectToSta() {
    onRedirect({ pluginName, moduleName })
  }

  // TODO: Backend should handle permissions for legacy menu
  if (
    pluginName === LegacyMenuRedirectRoute.InstructorLed &&
    simulatorTrainingInstructorLedMenuDenied
  )
    return null
  if (pluginName === LegacyMenuRedirectRoute.StudentLed && simulatorTrainingStudentLedMenuDenied)
    return null

  return (
    <ListItem>
      <Typography
        variant='small'
        title={text}
        onClick={handleRedirectToSta}
        data-test={`SidebarNavBranchItem${moduleName}`}
      >
        {text}
      </Typography>
    </ListItem>
  )
}

type NavBranchProps = {
  menuItem: LegacyMenuItem
  counter: Nullable<number>
  onRedirect: (redirectParams: StaRedirectParams) => void
}

const NavBranch: React.FC<NavBranchProps> = ({ menuItem, counter, onRedirect }) => {
  const { text, children, pluginName, moduleName } = menuItem
  const [isDroppedDown, setDropDown] = useState(false)
  const virtualClassroomMenuDenied = PermissionUtils.virtualClassroomMenuDenied()
  const virtualClassroomManageEventsDenied = PermissionUtils.virtualClassroomManageEventsDenied()
  const simulatorTrainingDenied = PermissionUtils.simulatorTrainingDenied()
  const simulatorTrainingBothSLILDenied =
    PermissionUtils.simulatorTrainingInstructorLedMenuDenied() &&
    PermissionUtils.simulatorTrainingStudentLedMenuDenied()
  const { type: accountType } = UserUtils.getUserAccountInfo()
  const isTrainee = accountType === AccountTypes.User

  const handleToggleDropdown = useCallback(() => {
    setDropDown(!isDroppedDown)
  }, [isDroppedDown])

  const handleRedirectToSta = useCallback(() => {
    onRedirect({ pluginName, moduleName })
  }, [onRedirect, pluginName, moduleName])

  function renderCounter() {
    if (!counter) return null
    return <Counter>{counter}</Counter>
  }

  // TODO: Backend should handle permissions for legacy menu
  if (pluginName === LegacyMenuRedirectRoute.VirtualClassroom && virtualClassroomMenuDenied)
    return null
  if (
    pluginName === LegacyMenuRedirectRoute.SimulatorTraining &&
    (simulatorTrainingDenied || simulatorTrainingBothSLILDenied || isTrainee)
  )
    return null
  if (pluginName === LegacyMenuRedirectRoute.Home && isTrainee) return null

  if (
    pluginName === LegacyMenuRedirectRoute.VirtualTrainerPlugin &&
    virtualClassroomManageEventsDenied
  )
    return null

  if (!children?.length) {
    return (
      <BranchWrapper>
        <Title>
          <Typography
            variant='small'
            onClick={handleRedirectToSta}
            title={text}
            data-test={`SidebarNavBranch${pluginName}`}
          >
            {text}
            {renderCounter()}
          </Typography>
        </Title>
      </BranchWrapper>
    )
  }

  return (
    <BranchWrapper>
      <Title isDroppedDown={isDroppedDown}>
        <Typography
          variant='small'
          title={text}
          onClick={handleToggleDropdown}
          data-test={`SidebarNavBranch${pluginName}`}
        >
          {text}
          {renderCounter()}
        </Typography>
        <Icon icon='chevron' data-test='SidebarNavBranchChevron' />
      </Title>
      <List isShown={isDroppedDown} data-test='SidebarNavBranchList'>
        {children.map(subMenuItem => (
          <NavBranchItem key={subMenuItem.text} item={subMenuItem} onRedirect={onRedirect} />
        ))}
      </List>
    </BranchWrapper>
  )
}

type NavTreeProps = {
  items?: LegacyMenuItem[]
  counters?: NotificationsNumbers
  onRedirect: (redirectParams: StaRedirectParams) => void
}

const NavTree: React.FC<NavTreeProps> = ({ items, counters, onRedirect }) => {
  if (!items) return null
  const menuItems = items

  function getMenuItemCounter(pluginName: string): Nullable<number> {
    const {
      allNotificationsCount,
      allPersonnelCount,
      allPublicationsCount,
      pproPendingTasksCount,
    } = (counters || {}) as NotificationsNumbers
    switch (pluginName) {
      case 'TaskStatusPlugin':
        return allNotificationsCount
      case 'PersonnelPlugin':
        return allPersonnelCount
      case 'LearningResourcesPlugin':
        return allPublicationsCount
      case 'PerformanceAppraisalsPlugin':
        return pproPendingTasksCount
      default:
        return null
    }
  }

  function renderPluginItems(isShown: boolean): ReactNode {
    if (!isShown) return null
    return (
      <>
        <DigitalDeliveryMenuItem />
        <AssessmentPlatformMenuItem />
      </>
    )
  }

  function renderProgrammes(isShown: boolean): ReactNode {
    if (!isShown) return null
    return <ProgramBuilderMenuItem />
  }

  function getFallbackPluginName(): Nullable<string> {
    if (menuItems.some(item => item.pluginName === 'TaskStatusPlugin')) {
      return 'TaskStatusPlugin'
    }
    if (menuItems.some(item => item.pluginName === 'LearningResourcesPlugin')) {
      return 'LearningResourcesPlugin'
    }
    if (menuItems.some(item => item.pluginName === 'Ocean_Help')) {
      return 'Ocean_Help'
    }
    return null
  }

  const fallbackPluginName = getFallbackPluginName()
  const fallbackPluginIndex =
    fallbackPluginName === null
      ? -1
      : menuItems.findIndex(item => item.pluginName === fallbackPluginName)

  return (
    <Wrapper>
      {menuItems.map((menuItem, index) => {
        const shouldRenderSupplementaryItems = index === fallbackPluginIndex

        return (
          <Fragment key={`${menuItem.pluginName}-${index}`}>
            {renderProgrammes(shouldRenderSupplementaryItems)}
            <NavBranch
              counter={getMenuItemCounter(menuItem.pluginName)}
              menuItem={menuItem}
              onRedirect={onRedirect}
              data-test='SidebarNavBranch'
            />
            {renderPluginItems(shouldRenderSupplementaryItems)}
          </Fragment>
        )
      })}

      {fallbackPluginName === null && (
        <>
          {renderProgrammes(true)}
          {renderPluginItems(true)}
        </>
      )}
    </Wrapper>
  )
}

export default NavTree
